'use client'

import { useProductCardAnalytics } from '@/feature/product/hooks/useProductAnalytics'
import { getProductDisplayPrice } from '@/feature/product/utils/productDisplayPrice'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Tag, type ProductRowLinkedProducts_ProductFragment } from '@gql-generated/graphql'
import { memo, useCallback, useState } from 'react'
import { ProductRow } from '../../product-row/ProductRow.client'
import type { ProductRowOnQuantityChangeInfos } from '../../product-row/_components/ProductRowControls.client'

type LinkedProduct = NonNullable<
  NonNullable<ProductRowLinkedProducts_ProductFragment['linkedProducts']>[number]
>

type TrackedLinkedProductRowProps = {
  product: LinkedProduct
  index: number
  productRef: React.RefObject<HTMLDivElement | null>
  onAddProductToCart: (product: { sku: string; quantity: number; price: number }) => Promise<void>
  onAddProductToCartFailed: () => void
}

export const TrackedLinkedProductRow = memo(function TrackedLinkedProductRow({
  product,
  index,
  productRef,
  onAddProductToCart,
  onAddProductToCartFailed,
}: TrackedLinkedProductRowProps) {
  const { t } = useTranslation(['productTag'])
  const packaging = product.stock?.packaging
  // Same starting quantity as the product card quick add, so the picker and the price stay in sync
  const [quantity, setQuantity] = useState(
    packaging?.minimalSalesQuantity ?? packaging?.incrementQuantity ?? packaging?.quantity ?? 1,
  )
  const unitPriceExclTax = product.price?.exclTax || 0
  const displayPrice = getProductDisplayPrice(product)
  // A calculated price is expressed per sales quantity, not per unit
  const totalExclTax = displayPrice.priceQuantity
    ? (displayPrice.price * quantity) / displayPrice.priceQuantity
    : unitPriceExclTax * quantity

  const { trackProductPageAnalytics } = useProductCardAnalytics({
    product,
    position: index + 1,
    isAuthenticated: true,
    source: 'produits complémentaires - page panier',
  })

  const handleQuantityChange = useCallback(async (infos: ProductRowOnQuantityChangeInfos) => {
    setQuantity(infos.quantity)
  }, [])

  return (
    <div key={product.reference} ref={productRef} data-product-id={product.reference}>
      <ProductRow
        id={`linked-product-${index}`}
        isInDrawer
        quantity={quantity}
        onQuantityChange={handleQuantityChange}
        totalExclTax={totalExclTax}
        unitPrice={{ exclTax: unitPriceExclTax }}
        product={{
          ...product,
          isLinkedProductsLinkVisible: false,
          canAddToCart: true,
          stock: {
            ...product.stock,
            isPriceAvailable: true,
          },
        }}
        discount={
          product.price?.discountExclTax || product.price?.discountPercentage
            ? {
                catalogDiscountAmount: product.price.discountExclTax,
                percentage: product.price.discountPercentage,
              }
            : undefined
        }
        promotionTag={
          product.price?.tag &&
          [
            Tag.Sale,
            Tag.Clearance,
            Tag.Loyalty,
            Tag.Refurbished,
            Tag.QuantitySale,
            Tag.WebSale,
          ].includes(product.price.tag)
            ? {
                label: t(`productTag:${product.price.tag}`),
                type: product.price.tag,
              }
            : undefined
        }
        displayAddToCartButton
        displayQuantityPicker
        onAddProductToCart={onAddProductToCart}
        onAddProductToCartFailed={onAddProductToCartFailed}
        onLinkPress={trackProductPageAnalytics}
      />
    </div>
  )
})
